import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useTransitionValue from 'react-transition-value';
import {
  Line,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  ComposedChart,
  Area,
  ReferenceDot,
} from 'recharts';
import { ChartLegend } from '../../../../../../../../components/chart-legend/chart-legend';
import { Header } from '../../../../../../../../components/header';
import { roundToOne } from '../../../../../../../../utils/helpers/number';
import { Triangle } from '../triangle/triangle';

type LesserChartProps = {
  value: number;
  hysteresis: number;
  thresholdUnit: string;
};

export const LesserChart: React.FC<LesserChartProps> = ({ value, hysteresis, thresholdUnit }) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const [isAnimating, setIsAnimating] = useState(false);
  const [leftDotValue, setLeftDotValue] = useTransitionValue(value);
  const [rightDotValue, setRightDotValue] = useTransitionValue(value + roundToOne(hysteresis));

  useEffect(() => {
    setIsAnimating(true);

    const left = value;
    const right = value + roundToOne(hysteresis);

    setLeftDotValue(left, { from: leftDotValue, duration: 380 });
    setRightDotValue(right, { from: rightDotValue, duration: 380 });

    const timer = setTimeout(() => setIsAnimating(false), 400);
    return () => clearTimeout(timer);
  }, [value, hysteresis]);

  const domainMin = Math.min(leftDotValue, rightDotValue) - 15;
  const domainMax = Math.max(leftDotValue, rightDotValue) + 15;

  const data = [
    { name: domainMin, value1: -1, value2: -1, value4: -1 },
    { name: domainMin + 5, value1: -1, value2: -1, value4: -1 },
    { name: leftDotValue, value1: -1, value2: 1, value4: -1 },
    { name: rightDotValue, value1: 1, value2: 1, value3: 1 },
    { name: domainMax - 5, value1: 1, value2: 1, value3: 1 },
    { name: domainMax, value1: 1, value2: 1, value3: 1 },
  ];

  const ticks = data.map((d) => d.name);

  return (
    <>
      <Header title={t('meterConfiguration.thresholdEvent.hysteresis')} />
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            unit={thresholdUnit}
            type="number"
            domain={[domainMin, domainMax]}
            ticks={ticks}
            tick={isAnimating ? false : undefined}
          />
          <Line type="stepAfter" dataKey="value1" stroke="var(--color-type-primary)" strokeWidth={2} dot={false} />
          <Line type="stepAfter" dataKey="value2" stroke="var(--color-type-primary)" strokeWidth={2} dot={false} />
          <Area type="monotone" dataKey="value3" stroke="var(--color-type-primary)" fill="var(--color-red)" />
          <Area type="monotone" dataKey="value4" stroke="var(--color-type-primary)" fill="var(--color-green)" />
          <ReferenceDot
            x={leftDotValue}
            y={0.35}
            r={10}
            fill="var(--color-green)"
            shape={(props) => <Triangle {...props} upsideDown={true} />}
          />
          <ReferenceDot
            x={rightDotValue}
            y={0.65}
            r={10}
            fill="var(--color-red)"
            shape={(props) => <Triangle {...props} />}
          />
          <ReferenceLine y={0} stroke="var(--color-type-primary)" strokeWidth={1.5} />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartLegend
        className="m-t-16"
        items={[
          { label: tc('status.off'), color: 'var(--color-red)' },
          { label: tc('status.on'), color: 'var(--color-green)' },
        ]}
      />
    </>
  );
};
